/**
 * CommitPipelineTiming — Wave 7 OBSERVABILITY
 *
 * Renders the commit-pipeline timing breakdown for the RPM tab:
 *   1. Per-step p50/p95 latency table (with attempts + success rate)
 *   2. Top-failing-step bar chart (inline SVG, matches Wave 6.2 pattern)
 *   3. Drill-through links to the carts where step X failed within range
 *
 * Data source: GET /v3/metrics/commit-timing?range=…
 *
 * Drill-through routes to /metrics/carts?range=…&pipelineStepFailed=<index>
 * — the existing DrillThroughRecord surface accepts an additional filter
 * key (sibling agent owns the back-end filter; this component just builds
 * the URL).
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useApiClient } from '@/contexts/ApiClientContext';
import type {
  CommitTimingMetricsData,
  CommitTimingMetricsResponse,
  CommitTimingPerStepRow,
  CommitTimingRange,
} from '@retail-ops/shared-types';

interface CommitPipelineTimingProps {
  range: CommitTimingRange;
}

export function CommitPipelineTiming({ range }: CommitPipelineTimingProps): JSX.Element {
  const client = useApiClient();
  const navigate = useNavigate();

  const [data, setData] = useState<CommitTimingMetricsData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const res: CommitTimingMetricsResponse = await client.retail.metrics.commitTiming(range);
        if (cancelled) return;
        if (res.success && res.data) {
          setData(res.data);
        } else {
          setError(res.error ?? 'No commit-timing data.');
        }
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : 'Commit-timing request failed.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, range]);

  const sortedByIndex = useMemo<ReadonlyArray<CommitTimingPerStepRow>>(() => {
    if (!data) return [];
    return [...data.perStep].sort((a, b) => a.stepIndex - b.stepIndex);
  }, [data]);

  const topFailing = useMemo(() => {
    if (!data) return [];
    return [...data.perStep]
      .filter((s) => s.failedCount > 0)
      .sort((a, b) => b.failedCount - a.failedCount)
      .slice(0, 5);
  }, [data]);

  function drillToFailedCarts(stepIndex: number): void {
    const params = new URLSearchParams();
    params.set('range', range);
    params.set('pipelineStepFailed', String(stepIndex));
    navigate(`/metrics/carts?${params.toString()}`);
  }

  if (loading && !data) {
    return (
      <div style={{ padding: 'var(--sp-6)', fontSize: 13, color: 'var(--ink-600)' }}>
        Loading commit-timing…
      </div>
    );
  }
  if (error) {
    return (
      <div className="rpm-alert-banner" role="alert" style={{ margin: 'var(--sp-4)' }}>
        <span>
          <strong>● Commit-timing error:</strong> {error}
        </span>
      </div>
    );
  }
  if (!data || sortedByIndex.length === 0) {
    return (
      <div style={{ padding: 'var(--sp-6)', fontSize: 13, color: 'var(--ink-600)' }}>
        No commit-pipeline activity in the selected range.
      </div>
    );
  }

  const maxFail = Math.max(1, ...topFailing.map((t) => t.failedCount));

  return (
    <div className="commit-pipeline-timing" style={{ padding: 'var(--sp-4)', fontSize: 13 }}>
      {/* ── Totals strip ───────────────────────────────────────────── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 'var(--sp-4)',
          marginBottom: 'var(--sp-6)',
        }}
      >
        <SummaryStat
          label="Completed commits"
          value={data.totals.completedCommits.toLocaleString()}
        />
        <SummaryStat
          label="Aborted commits"
          value={data.totals.abortedCommits.toLocaleString()}
          tone={data.totals.abortedCommits > 0 ? 'warn' : 'neutral'}
        />
        <SummaryStat
          label="Step attempts"
          value={data.totals.totalStepAttempts.toLocaleString()}
        />
        <SummaryStat
          label="Step failures"
          value={data.totals.totalStepFailures.toLocaleString()}
          tone={data.totals.totalStepFailures > 0 ? 'warn' : 'neutral'}
        />
      </div>

      {/* ── Per-step latency table ────────────────────────────────── */}
      <div style={{ marginBottom: 'var(--sp-6)' }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--ink-600)',
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            marginBottom: 'var(--sp-2)',
          }}
        >
          Per-step latency (p50 / p95) · success rate
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--ink-600)', fontSize: 11 }}>
              <th style={{ padding: '6px 8px' }}>#</th>
              <th style={{ padding: '6px 8px' }}>Step</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Attempts</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Success</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>p50</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>p95</th>
              <th style={{ padding: '6px 8px' }}>Top error</th>
            </tr>
          </thead>
          <tbody>
            {sortedByIndex.map((row) => (
              <tr
                key={`${row.stepIndex}-${row.stepName}`}
                style={{ borderTop: '1px solid var(--surface-border)' }}
              >
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{row.stepIndex}</td>
                <td style={{ padding: '6px 8px' }}>
                  <strong>{humanStepName(row.stepName)}</strong>
                  <div style={{ fontSize: 11, color: 'var(--ink-500)' }}>{row.stepName}</div>
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'right' }}>{row.attempts}</td>
                <td
                  style={{
                    padding: '6px 8px',
                    textAlign: 'right',
                    color: row.successRate < 0.95 ? 'var(--pcb-red)' : 'var(--ink-700)',
                  }}
                >
                  {formatPct(row.successRate)}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                  {formatMs(row.p50Ms)}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                  {formatMs(row.p95Ms)}
                </td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 11 }}>
                  {row.topErrorCode
                    ? `${row.topErrorCode.errorCode} (${row.topErrorCode.count})`
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Top-failing step bar chart ────────────────────────────── */}
      {topFailing.length > 0 && (
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--ink-600)',
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              marginBottom: 'var(--sp-2)',
            }}
          >
            Top failing steps · click bar to drill into failed carts
          </div>
          <svg
            width="100%"
            height={Math.max(80, topFailing.length * 30 + 16)}
            role="img"
            aria-label="Top failing pipeline steps"
            style={{ display: 'block' }}
          >
            {topFailing.map((row, idx) => {
              const y = idx * 30 + 8;
              const widthPct = (row.failedCount / maxFail) * 100;
              const labelText = `${row.stepIndex}. ${humanStepName(row.stepName)}`;
              return (
                <g
                  key={`${row.stepIndex}-${row.stepName}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => drillToFailedCarts(row.stepIndex)}
                  role="button"
                  aria-label={`Drill to ${labelText} failures (${row.failedCount})`}
                >
                  <text x={4} y={y + 13} fontSize={12} fill="var(--ink-700)">
                    {labelText}
                  </text>
                  <rect
                    x="32%"
                    y={y}
                    width={`${widthPct * 0.6}%`}
                    height={20}
                    fill="var(--pcb-red, #b10505)"
                    opacity={0.85}
                    rx={2}
                  />
                  <text
                    x={`calc(32% + ${widthPct * 0.6}% + 6px)`}
                    y={y + 14}
                    fontSize={12}
                    fill="var(--ink-700)"
                  >
                    {row.failedCount} / {row.attempts}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      )}

      {data.topFailingStep && (
        <div
          style={{
            marginTop: 'var(--sp-4)',
            padding: 'var(--sp-3)',
            background: 'var(--surface-subtle, #fafafa)',
            border: '1px solid var(--surface-border)',
            borderRadius: 4,
            fontSize: 12,
          }}
        >
          <strong>Top failing step:</strong>{' '}
          {data.topFailingStep.stepIndex}. {humanStepName(data.topFailingStep.stepName)} —{' '}
          {data.topFailingStep.failedCount} failures across {data.topFailingStep.attempts} attempts.{' '}
          <button
            type="button"
            onClick={() => drillToFailedCarts(data.topFailingStep!.stepIndex)}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--pcb-primary, var(--pcb-blue))',
              cursor: 'pointer',
              padding: 0,
              textDecoration: 'underline',
              fontSize: 12,
            }}
          >
            View failed carts →
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function formatPct(rate: number): string {
  if (!Number.isFinite(rate)) return '—';
  return `${Math.round(rate * 1000) / 10}%`;
}

function formatMs(ms: number | null): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}m`;
}

function humanStepName(name: string): string {
  // Friendly labels for the canonical 1–11 step names.
  const map: Record<string, string> = {
    'pre-commit-validation': 'Pre-commit validation',
    'cif-create': 'CIF create',
    'acct-add': 'Account create',
    'beneficiary-attach': 'Beneficiary attach',
    'disclosure-prefill': 'Disclosure pre-fill',
    'signnow-dispatch': 'Signing dispatch',
    'await-signatures': 'Await signatures',
    'post-sign-jxchange': 'Post-sign jXchange',
    'qc-dataverse-write': 'QC Dataverse write',
    notifications: 'Notifications',
    'resolution-stamp': 'Resolution stamp',
  };
  return map[name] ?? name;
}

interface SummaryStatProps {
  label: string;
  value: string;
  tone?: 'neutral' | 'warn' | 'good';
}

function SummaryStat({ label, value, tone = 'neutral' }: SummaryStatProps): JSX.Element {
  const color =
    tone === 'warn'
      ? 'var(--pcb-red, #b10505)'
      : tone === 'good'
      ? 'var(--pcb-green, #386732)'
      : 'var(--ink-800)';
  return (
    <div
      style={{
        padding: 'var(--sp-3)',
        background: 'var(--surface-subtle, #fafafa)',
        border: '1px solid var(--surface-border)',
        borderRadius: 4,
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: 'var(--ink-600)',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 18, fontWeight: 600, color, marginTop: 2 }}>{value}</div>
    </div>
  );
}
